import React, { useState, useEffect, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Search, SlidersHorizontal, X, ChevronDown, ShoppingBag } from "lucide-react";
import { http } from "../../lib/api";
import { useCart } from "../../context/CartContext";
import { toast } from "sonner";

const PAGE_SIZE = 20;

function ProductCard({ product }) {
  const { addItem } = useCart();
  const price = product.price != null ? `${product.currency || "$"}${Number(product.price).toFixed(2)}` : "";
  const comparePrice = product.compare_price ? `${product.currency || "$"}${Number(product.compare_price).toFixed(2)}` : null;
  const oos = product.stock !== undefined && product.stock === 0;

  return (
    <Link to={`/store/product/${product.id}`} className="group block">
      <div className="rounded-2xl overflow-hidden bg-white border border-gray-100 hover:shadow-md transition-shadow">
        <div className="relative aspect-square bg-gray-50 overflow-hidden">
          {product.images?.[0]
            ? <img src={product.images[0]} alt={product.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
            : <div className="w-full h-full grid place-items-center"><ShoppingBag size={36} className="text-gray-200" /></div>
          }
          {product.badge && (
            <span className="absolute top-2 left-2 px-2 py-0.5 rounded-full text-xs font-bold text-white" style={{ background: "#C5A028" }}>{product.badge}</span>
          )}
          {oos && <div className="absolute inset-0 bg-white/60 grid place-items-center"><span className="text-xs font-bold text-gray-500 bg-white px-3 py-1 rounded-full border">Out of stock</span></div>}
          {!oos && !product.variants?.length && (
            <button
              onClick={e => { e.preventDefault(); addItem(product); toast.success("Added to cart!"); }}
              className="absolute bottom-2 inset-x-2 py-2 rounded-xl text-xs font-bold text-white opacity-0 group-hover:opacity-100 transition-opacity"
              style={{ background: "#0F1E38" }}
            >Add to Cart</button>
          )}
        </div>
        <div className="p-3">
          <div className="font-semibold text-gray-900 text-sm leading-snug line-clamp-2 mb-1.5">{product.name}</div>
          <div className="flex items-center gap-2">
            <span className="font-bold text-gray-900">{price}</span>
            {comparePrice && <span className="text-xs text-gray-400 line-through">{comparePrice}</span>}
          </div>
          {product.variants?.length > 0 && (
            <div className="text-xs text-gray-400 mt-1">{product.variants.length} options</div>
          )}
        </div>
      </div>
    </Link>
  );
}

export default function StoreCatalog() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [collections, setCollections] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [showFilters, setShowFilters] = useState(false);

  const q = searchParams.get("q") || "";
  const collectionSlug = searchParams.get("collection") || "";
  const featured = searchParams.get("featured") || "";
  const sort = searchParams.get("sort") || "newest";
  const minPrice = searchParams.get("min") || "";
  const maxPrice = searchParams.get("max") || "";

  const [localQ, setLocalQ] = useState(q);

  const load = useCallback(() => {
    setLoading(true);
    const params = { limit: PAGE_SIZE, offset: (page - 1) * PAGE_SIZE, sort };
    if (q) params.q = q;
    if (collectionSlug) params.collection = collectionSlug;
    if (featured) params.featured = true;
    if (minPrice) params.min_price = minPrice;
    if (maxPrice) params.max_price = maxPrice;
    http.get("/store/products", { params })
      .then(r => { setProducts(r.data.items || r.data); setTotal(r.data.total || r.data.length); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [q, collectionSlug, featured, sort, minPrice, maxPrice, page]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    http.get("/store/collections").then(r => setCollections(r.data)).catch(() => {});
  }, []);

  const setParam = (key, val) => {
    const next = new URLSearchParams(searchParams);
    if (val) next.set(key, val); else next.delete(key);
    next.delete("page");
    setPage(1);
    setSearchParams(next);
  };

  const activeCollection = collections.find(c => c.slug === collectionSlug);
  const hasMore = products.length >= PAGE_SIZE;

  const SORT_OPTIONS = [
    { value: "newest", label: "Newest" },
    { value: "price_asc", label: "Price: Low to High" },
    { value: "price_desc", label: "Price: High to Low" },
    { value: "popular", label: "Most Popular" },
  ];

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900" style={{ fontFamily: "Cormorant Garamond, serif" }}>
            {activeCollection?.name || (q ? `Search: "${q}"` : featured ? "Featured Products" : "All Products")}
          </h1>
          {total > 0 && <div className="text-sm text-gray-500 mt-1">{total} product{total !== 1 ? "s" : ""}</div>}
        </div>
        <div className="flex items-center gap-3">
          <button onClick={() => setShowFilters(o => !o)} className="flex items-center gap-2 px-4 py-2 rounded-xl border border-gray-200 text-sm font-semibold text-gray-700 hover:bg-gray-50 transition-colors">
            <SlidersHorizontal size={15} /> Filters
          </button>
          <div className="relative">
            <select
              value={sort}
              onChange={e => setParam("sort", e.target.value)}
              className="pl-3 pr-8 py-2 rounded-xl border border-gray-200 text-sm font-semibold text-gray-700 appearance-none bg-white cursor-pointer"
            >
              {SORT_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
            <ChevronDown size={13} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
          </div>
        </div>
      </div>

      <div className="flex gap-6">
        {/* Sidebar filters */}
        {showFilters && (
          <aside className="w-56 shrink-0 space-y-6">
            <div className="bg-white rounded-2xl border border-gray-100 p-4 space-y-4">
              <div className="flex items-center justify-between">
                <div className="font-semibold text-gray-900 text-sm">Filters</div>
                <button onClick={() => setShowFilters(false)}><X size={14} className="text-gray-400" /></button>
              </div>
              {/* Search */}
              <div>
                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2 block">Search</label>
                <form onSubmit={e => { e.preventDefault(); setParam("q", localQ); }}>
                  <div className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2">
                    <Search size={13} className="text-gray-400" />
                    <input value={localQ} onChange={e => setLocalQ(e.target.value)} placeholder="Search…" className="text-sm bg-transparent outline-none flex-1" />
                  </div>
                </form>
              </div>
              {/* Collections */}
              <div>
                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2 block">Collections</label>
                <div className="space-y-1">
                  <button onClick={() => setParam("collection", "")} className={`w-full text-left px-2 py-1.5 rounded-lg text-sm transition-colors ${!collectionSlug ? "font-bold text-yellow-600" : "text-gray-700 hover:bg-gray-50"}`}>All</button>
                  {collections.map(c => (
                    <button key={c.id} onClick={() => setParam("collection", c.slug)} className={`w-full text-left px-2 py-1.5 rounded-lg text-sm transition-colors ${collectionSlug === c.slug ? "font-bold text-yellow-600" : "text-gray-700 hover:bg-gray-50"}`}>{c.name}</button>
                  ))}
                </div>
              </div>
              {/* Price range */}
              <div>
                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2 block">Price Range</label>
                <div className="flex items-center gap-2">
                  <input type="number" placeholder="Min" value={minPrice} onChange={e => setParam("min", e.target.value)} className="w-full px-2 py-1.5 text-sm border border-gray-200 rounded-lg outline-none" />
                  <span className="text-gray-400">–</span>
                  <input type="number" placeholder="Max" value={maxPrice} onChange={e => setParam("max", e.target.value)} className="w-full px-2 py-1.5 text-sm border border-gray-200 rounded-lg outline-none" />
                </div>
              </div>
            </div>
          </aside>
        )}

        <div className="flex-1 min-w-0 space-y-6">
          {/* Collection tabs — horizontal scroll on mobile */}
          {collections.length > 0 && !showFilters && (
            <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-none">
              <button onClick={() => setParam("collection", "")} className={`shrink-0 px-4 py-2 rounded-full text-sm font-semibold border transition-colors ${!collectionSlug ? "text-white border-transparent" : "border-gray-200 text-gray-600 bg-white hover:border-gray-300"}`} style={!collectionSlug ? { background: "#0F1E38" } : {}}>All</button>
              {collections.map(c => (
                <button key={c.id} onClick={() => setParam("collection", c.slug)} className={`shrink-0 px-4 py-2 rounded-full text-sm font-semibold border transition-colors ${collectionSlug === c.slug ? "text-white border-transparent" : "border-gray-200 text-gray-600 bg-white hover:border-gray-300"}`} style={collectionSlug === c.slug ? { background: "#0F1E38" } : {}}>{c.name}</button>
              ))}
            </div>
          )}

          {loading ? (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 animate-pulse">
              {Array.from({ length: 8 }).map((_, i) => <div key={i} className="aspect-square rounded-2xl bg-gray-100" />)}
            </div>
          ) : products.length === 0 ? (
            <div className="text-center py-20">
              <ShoppingBag size={40} className="mx-auto text-gray-300 mb-4" />
              <div className="text-lg font-semibold text-gray-500">No products found</div>
              <div className="text-sm text-gray-400 mt-1">Try a different collection or search term</div>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {products.map(p => <ProductCard key={p.id} product={p} />)}
              </div>
              {hasMore && (
                <div className="text-center pt-4">
                  <button onClick={() => setPage(p => p + 1)} className="px-8 py-3 rounded-full border border-gray-300 font-semibold text-sm text-gray-700 hover:bg-gray-50 transition-colors">
                    Load More
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
